// components/EnhancedUserBubble.js - Pure JSX component for enhanced user bubble
// Extracted from PlatformSelector.js and Dashboard.js

import React from 'react';
import { useEnhancedUserBubble } from '../hooks/useEnhancedUserBubble';

/**
 * Enhanced User Bubble Component - Pure JSX with no business logic
 */
export const EnhancedUserBubble = ({ 
  navigate,
  formatTime,
  guestTimeRemaining,
  platformStates,
  onPlatformLogout,
  onMongoLogout,
  onBackToPlatforms,
  showBackToPlatforms = false,
  currentPage = 'platforms',
  // Platform action handlers passed from parent
  handleSpotifyAction,
  handleYouTubeAction
}) => {
  // Use the enhanced user bubble hook for all logic
  const {
    isGuest,
    showMongoDropdown,
    showSpotifyDropdown,
    showYouTubeDropdown,
    showMongoLogoutConfirm,
    getUserDisplayName,
    getUserAvatarLetter,
    getUserEmailOrSession,
    handleBubblePlatformLogout,
    handleBackToPlatforms,
    handleMongoLogoutConfirm,
    handleCreateAccount,
    handleShowLogoutConfirm,
    handleCancelLogout,
    toggleMongoDropdown,
    toggleSpotifyDropdown,
    toggleYouTubeDropdown
  } = useEnhancedUserBubble(navigate, currentPage);

  return (
    <div className="user-bubble-container">
      <div className="enhanced-user-bubble">
        
        {/* MongoDB Account Section */}
        <div className="mongo-account-section">
          <div 
            className="mongo-account-icon"
            onClick={toggleMongoDropdown}
          >
            <div className="mongo-avatar">
              {getUserAvatarLetter()}
            </div>
            <span className="mongo-account-name">
              {getUserDisplayName()}
            </span>
            {isGuest && guestTimeRemaining && (
              <span className="guest-timer">
                ({formatTime(guestTimeRemaining)})
              </span>
            )}
          </div>

          {/* MongoDB Dropdown */}
          {showMongoDropdown && (
            <div className="mongo-dropdown">
              <div className="dropdown-header">
                <div className="account-info">
                  <div className="account-name">
                    {getUserDisplayName()}
                  </div>
                  <div className="account-email">
                    {getUserEmailOrSession()}
                  </div>
                </div>
              </div>
              
              <div className="dropdown-actions">
                {currentPage === 'dashboard' && (
                  <button 
                    className="dropdown-action-btn back-to-platforms"
                    onClick={() => handleBackToPlatforms(onBackToPlatforms)}
                  >
                    🏠 Back to Platforms
                  </button>
                )}
                
                {currentPage === 'platforms' && (
                  <button 
                    className="dropdown-action-btn back-to-platforms"
                    onClick={() => handleBackToPlatforms(onBackToPlatforms)}
                  >
                    🏠 On Platforms Page
                  </button>
                )}
                
                {!isGuest && (
                  <button 
                    className="dropdown-action-btn logout-btn"
                    onClick={handleShowLogoutConfirm}
                  >
                    🚪 Logout Account
                  </button>
                )}
                
                {isGuest && (
                  <button 
                    className="dropdown-action-btn create-account-btn"
                    onClick={handleCreateAccount}
                  >
                    ➕ Create Account
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Platform Icons Section */}
        <div className="platform-icons-section">
          
          {/* Spotify Icon & Dropdown */}
          {platformStates.spotify && platformStates.spotify.connected && (
            <div className="platform-icon-container">
              <div 
                className={`platform-bubble-icon spotify ${platformStates.spotify.active ? 'active' : ''}`}
                onClick={toggleSpotifyDropdown}
              >
                ♪
              </div>
              
              {showSpotifyDropdown && (
                <div className="platform-dropdown spotify-dropdown">
                  <div className="platform-dropdown-header">
                    <div className="platform-name">Spotify</div>
                    <div className="platform-username">
                      {platformStates.spotify.username || 'Connected'}
                    </div>
                  </div>
                  
                  <div className="platform-dropdown-actions">
                    <button 
                      className="dropdown-action-btn switch-btn"
                      onClick={() => handleSpotifyAction(navigate)}
                    >
                      🎵 Go to Dashboard
                    </button>
                    
                    <button 
                      className="dropdown-action-btn logout-btn"
                      onClick={() => handleBubblePlatformLogout('spotify', onPlatformLogout)}
                    >
                      🚪 Logout Spotify
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* YouTube Icon & Dropdown */}
          {platformStates.youtube && platformStates.youtube.connected && (
            <div className="platform-icon-container">
              <div 
                className={`platform-bubble-icon youtube ${platformStates.youtube.active ? 'active' : ''}`}
                onClick={toggleYouTubeDropdown}
              >
                ▶
              </div>
              
              {showYouTubeDropdown && (
                <div className="platform-dropdown youtube-dropdown">
                  <div className="platform-dropdown-header">
                    <div className="platform-name">YouTube Music</div>
                    <div className="platform-username">
                      {platformStates.youtube.username || 'Connected'}
                    </div>
                  </div>
                  
                  <div className="platform-dropdown-actions">
                    <button 
                      className="dropdown-action-btn switch-btn"
                      onClick={() => handleYouTubeAction(navigate)}
                    >
                      🎬 Go to Dashboard
                    </button>
                    
                    <button 
                      className="dropdown-action-btn logout-btn"
                      onClick={() => handleBubblePlatformLogout('youtube', onPlatformLogout)}
                    >
                      🚪 Logout YouTube
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
        </div>
      </div>

      {/* MongoDB Logout Confirmation Modal */}
      {showMongoLogoutConfirm && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Confirm Account Logout</h3>
            </div>
            <div className="modal-body">
              <p>This will:</p>
              <ul>
                <li>Save your preferences safely</li>
                <li>Clear all platform sessions (security)</li>
                <li>Log you out completely</li>
              </ul>
              <p><strong>Are you sure you want to logout?</strong></p>
            </div>
            <div className="modal-actions">
              <button 
                className="modal-btn cancel-btn"
                onClick={handleCancelLogout}
              >
                Cancel
              </button>
              <button 
                className="modal-btn confirm-btn"
                onClick={() => handleMongoLogoutConfirm(onMongoLogout)}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedUserBubble;